import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { shiftApi } from '../../../api/shiftApi';
import { employeeApi } from '../../../api/employeeApi';
import { DropdownItem, Employee } from '../../../types';
import { toast } from 'react-toastify';

const getEmployeeLabel = (employee: Employee) => `${employee.fullName} (${employee.employeeCode})`;

const AssignShiftToEmployee: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<DropdownItem[]>([]);
  const [employee, setEmployee] = useState('');
  const [shift, setShift] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    shiftApi
      .getShifts()
      .then((items) => setShifts(items.map((s: any) => ({ id: s.id, name: s.shiftName }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const timeoutId = window.setTimeout(async () => {
      setEmployeeLoading(true);
      try {
        const result = await employeeApi.getAll(1, 50, employeeSearchText.trim() || undefined);

        if (!isCancelled) {
          setEmployees(result);
        }
      } catch {
        if (!isCancelled) {
          setEmployees([]);
        }
      } finally {
        if (!isCancelled) {
          setEmployeeLoading(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [employeeSearchText]);

  const employeeOptions = useMemo(() => {
    if (!selectedEmployee) {
      return employees;
    }

    const alreadyIncluded = employees.some((item) => item.id === selectedEmployee.id);
    return alreadyIncluded ? employees : [selectedEmployee, ...employees];
  }, [employees, selectedEmployee]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee || !shift || !effectiveDate) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await shiftApi.assignShiftToEmployee({ employeeId: employee, shiftId: shift, effectiveDate });
      toast.success('Shift assigned successfully!');
      setEmployee('');
      setSelectedEmployee(null);
      setEmployeeSearchText('');
      setShift('');
      setEffectiveDate('');
    } catch (err: any) {
      const responseData = err.response?.data;
      toast.error(responseData?.details || responseData?.message || 'Failed to assign shift');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Assign Shift to Employee
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Assign a specific shift to an employee with an effective date.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                size="small"
                options={employeeOptions}
                value={selectedEmployee}
                inputValue={employeeSearchText}
                loading={employeeLoading}
                onChange={(_, newValue) => {
                  setSelectedEmployee(newValue);
                  setEmployee(newValue?.id ?? '');
                  setEmployeeSearchText(newValue ? getEmployeeLabel(newValue) : '');
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    setSelectedEmployee(null);
                    setEmployee('');
                    setEmployeeSearchText(newInputValue);
                  }

                  if (reason === 'clear') {
                    setSelectedEmployee(null);
                    setEmployee('');
                    setEmployeeSearchText('');
                  }
                }}
                getOptionLabel={getEmployeeLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No employees found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee *"
                    placeholder="Search employee by name or code"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeeLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Shift *</InputLabel>
                <Select
                  value={shift}
                  label="Shift *"
                  onChange={(e) => setShift(e.target.value)}
                >
                  <MenuItem value="">Select Shift</MenuItem>
                  {shifts.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Effective Date *"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                inputProps={{
                  min: today,
                }}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
            sx={{ mt: 3 }}
            disabled={submitting}
          >
            {submitting ? 'Assigning...' : 'Assign Shift'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssignShiftToEmployee;
